import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { AlertCircle, Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const { token } = useParams(); // Get token from URL params

  // Validate password in real-time
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    return errors;
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    if (newPassword) {
      const errors = validatePassword(newPassword);
      setPasswordErrors(errors);
    } else {
      setPasswordErrors([]);
    }
    
    // Clear any existing errors when user starts typing
    if (error) setError("");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    // Validation
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      setIsLoading(false);
      return;
    }

    if (!password || !confirmPassword) {
      setError("Please fill out all fields.");
      setIsLoading(false);
      return;
    }

    const passwordValidationErrors = validatePassword(password);
    if (passwordValidationErrors.length > 0) {
      setError(passwordValidationErrors[0]);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    try {
      const result = await resetPassword(token, password, confirmPassword);
      if (result.success) {
        setSuccess("Your password has been reset successfully! Redirecting to login...");
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/auth", { 
            state: { 
              message: "Password reset successful! Please log in with your new password.",
              type: "success"
            } 
          });
        }, 2000);
      } else {
        setError(result.error || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid = password && confirmPassword && passwordErrors.length === 0 && password === confirmPassword;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        {/* Back Button */}
        <div className="w-full max-w-md mb-4 flex justify-start">
          <button
            onClick={() => navigate("/auth")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            disabled={isLoading}
          >
            <ArrowLeft size={20} /> Back to Login
          </button>
        </div>

        {/* Title & Description */}
        <div className="max-w-md text-center mb-6">
          <h2 className="text-3xl font-bold mb-2">Set a New Password</h2>
          <p className="text-sm text-gray-600">
            Enter your new password below to reset your account password.
          </p>
        </div>

        {/* Card */}
        <div className="w-full max-w-md bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <form onSubmit={handleReset} className="space-y-4">
            {/* Password Input */}
            <div className="relative">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={handlePasswordChange}
                  className={`pl-12 pr-12 focus-visible:ring-igbanwo-mint mt-1 ${
                    passwordErrors.length > 0 && password ? 'border-red-300 focus-visible:ring-red-200' : ''
                  }`}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Password validation feedback */}
              {password && passwordErrors.length > 0 && (
                <p className="text-xs text-red-600 mt-1">{passwordErrors[0]}</p>
              )}
              {password && passwordErrors.length === 0 && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle size={12} /> Password meets requirements
                </p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div className="relative">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError("");
                  }}
                  className={`pl-12 pr-12 focus-visible:ring-igbanwo-mint mt-1 ${
                    confirmPassword && password !== confirmPassword ? 'border-red-300 focus-visible:ring-red-200' : ''
                  }`}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Password match feedback */}
              {confirmPassword && password && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${
                  password === confirmPassword ? 'text-green-600' : 'text-red-600'
                }`}>
                  {password === confirmPassword ? (
                    <>
                      <CheckCircle size={12} /> Passwords match
                    </>
                  ) : (
                    <>
                      <AlertCircle size={12} /> Passwords do not match
                    </>
                  )}
                </p>
              )}
            </div>

            {/* Error & Success Messages */}
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle size={16} className="flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {/* Reset Button */}
            <Button
              type="submit"
              className="w-full bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || !isFormValid}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Resetting Password...
                </div>
              ) : (
                "Reset Password"
              )}
            </Button>

            {/* Help text */}
            <div className="text-center text-xs text-gray-500 mt-4">
              <p>Having trouble? <button 
                type="button"
                onClick={() => navigate("/forgot-password")} 
                className="text-igbanwo-mint hover:underline"
                disabled={isLoading}
              >
                Request a new reset link
              </button></p>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;